import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { getPathWithQuery } from 'src/lib/http/query'
import styled from 'styled-components'
import { getCurrentPublicRuntimeConfig } from '../../utils/helpers/helpers'
import { Button } from '@awaze/design-system.components.button'

export const contentfulLink = (contentfulId: string): string => {
  const publicRuntimeConfig = getCurrentPublicRuntimeConfig()
  const env =
    publicRuntimeConfig.APP_ENV === 'prd'
      ? 'master'
      : publicRuntimeConfig.APP_ENV
  const contentfulSpaceId = publicRuntimeConfig.CONTENTFUL_SPACE_ID
  return `https://app.contentful.com/spaces/${contentfulSpaceId}/environments/${env}/entries/${contentfulId}`
}

const Banner = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  transform: rotate(45deg) translate(6rem);
  width: 20rem;
  height: 3rem;
  background: hotpink;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  border: 2px solid #fff;
  font-size: 1.7rem;
  box-shadow: hotpink 0px -2px 4px, hotpink 0px 2px 2px;
`

const Topbar = styled.div`
  top: 0;
  left: 0;
  z-index: 1000;
  position: sticky;
  width: 100%;
  background: hotpink;
  text-align: center;
  color: #fff;
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: center;
  > * + * {
    &:before {
      margin: 0 12px;
      content: '|';
    }
  }
`

const MarkContainer = styled.a<{
  top: number
  left: number
  height: number
  width: number
}>`
  position: absolute;
  height: 30px;
  width: 30px;
  border-radius: 5px;
  background: black;
  display: grid;
  place-items: center;
  padding: 5px;
  top: ${(p) => p.top}px;
  left: ${(p) => p.left}px;
  transform: translateY(-100%);
  z-index: 2000;

  :hover {
    &:before {
      content: 'View in contentful';
      display: grid;
      place-items: center;
      color: #ffffff;
      font-size: 40px;
      background: rgba(255, 105, 180, 0.5);
      position: absolute;
      border: 2px solid hotpink;
      height: ${(p) => p.height}px;
      width: ${(p) => p.width}px;
      transform: translate(calc(50% - 15px), calc(50% - 15px));
    }
  }
`

interface Position {
  top: number
  left: number
  height: number
  width: number
}

interface ContentfulElementPosition extends Position {
  contentfulId: string
}

function getOffset(element: HTMLElement): Position | undefined {
  let left = 0
  let top = 0
  let enumerator = element
  while (
    enumerator &&
    !isNaN(enumerator.offsetLeft) &&
    !isNaN(enumerator.offsetTop)
  ) {
    left += enumerator.offsetLeft - enumerator.scrollLeft
    top += enumerator.offsetTop - enumerator.scrollTop
    enumerator = enumerator.offsetParent as HTMLElement
  }
  return { top, left, height: element.offsetHeight, width: element.offsetWidth }
}

type Props = {
  contentfulId?: string
}

export const PreviewBanner: React.FC<Props> = ({ contentfulId }) => {
  const { asPath: pathWithQuery } = useRouter()

  const isTranslationDebugActive = pathWithQuery.includes(
    'isTranslationDebugActive=true',
  )

  const [links, setLinks] = useState<ContentfulElementPosition[]>([])

  const calculateLinks = () => {
    setLinks(
      Array.from(document.querySelectorAll('[data-contentfulid]')).map((x) => ({
        ...getOffset(x as HTMLElement),
        contentfulId: x.getAttribute('data-contentfulid'),
      })),
    )
  }

  const disablePreviewMode = async () => {
    const res = await fetch('/api/disable-preview-mode')
    if (res.status === 200) {
      window.location.reload()
    }
  }

  const toggleLinks = (on: boolean): void => {
    if (!on) {
      setLinks([])
    } else {
      calculateLinks()
    }
  }

  useEffect(() => {
    if (!links.length) return

    const observer = new MutationObserver(calculateLinks)
    observer.observe(document, {
      subtree: true,
      attributes: true,
    })
    return () => observer?.disconnect()
  }, [links.length])

  const pathWithIsTranslationDebugActive = getPathWithQuery(pathWithQuery, {
    isTranslationDebugActive: 'true',
  })

  const pathWithoutIsTranslationDebugActive = getPathWithQuery(pathWithQuery, {
    isTranslationDebugActive: 'false',
  })

  return (
    <>
      <Topbar>
        <Button
          variant="text"
          onClick={disablePreviewMode}
          text="Disable preview mode"
          colourScheme="reversed"
          style={{ textDecoration: 'none' }}
        />
        <label
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          {isTranslationDebugActive ? (
            <Link href={pathWithoutIsTranslationDebugActive}>
              See without translation IDs
            </Link>
          ) : (
            <Link href={pathWithIsTranslationDebugActive}>
              See with translation IDs
            </Link>
          )}
        </label>
        {contentfulId && (
          <>
            <label
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              Toggle contentful links
              <input
                style={{ marginLeft: 4 }}
                checked={!!links.length}
                onChange={(x) => toggleLinks(x.target.checked)}
                type="checkbox"
              />
            </label>
            <a
              href={contentfulLink(contentfulId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in contentful
            </a>
          </>
        )}
      </Topbar>
      <Banner>P R E V I E W</Banner>
      {links.length > 0 && (
        <div style={{ position: 'relative' }}>
          {links.map((link) => (
            <MarkContainer
              key={link.contentfulId}
              top={link.top}
              left={link.left}
              height={link.height}
              width={link.width}
              href={contentfulLink(link.contentfulId)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ContentfulLogo />
            </MarkContainer>
          ))}
        </div>
      )}
    </>
  )
}

const ContentfulLogo: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 165.06 165.06">
    <path
      d="M49.22 115.5A45.64 45.64 0 0 1 36 83.11a45.16 45.16 0 0 1 13.45-32.27 18 18 0 1 0-25.36-25.47 81.67 81.67 0 0 0-.35 115.49 18 18 0 1 0 25.48-25.36Z"
      style={{ fill: '#ffda00' }}
    ></path>
    <path
      d="M49.45 50.84a45.79 45.79 0 0 1 64.66.23 18 18 0 1 0 25.47-25.35 81.39 81.39 0 0 0-115.37-.47 18 18 0 1 0 25.24 25.59Z"
      style={{ fill: '#67b3ff' }}
    ></path>
    <path
      d="M114 115.73A45.68 45.68 0 0 1 81.6 129a45.16 45.16 0 0 1-32.27-13.5 18 18 0 1 0-25.47 25.36 81.54 81.54 0 0 0 115.49.46A18 18 0 0 0 114 115.73Z"
      style={{ fill: '#eb5a68' }}
    ></path>
    <path
      d="M49.45 50.84a18 18 0 1 1 .11-25.36 18 18 0 0 1-.11 25.36Z"
      style={{ fill: '#47a1ff' }}
    ></path>
    <path
      d="M49.1 140.86a18 18 0 1 1 .12-25.36 18 18 0 0 1-.12 25.36Z"
      style={{ fill: '#d5465f' }}
    ></path>
  </svg>
)
